/**
 * Unified Route Definitions
 *
 * This file serves as the single source of truth for all route configurations.
 * It defines paths, parse functions, and stringify functions in one place,
 * which are then used to:
 * 1. Generate the linking config for React Navigation (in SignedIn.tsx)
 * 2. Build href URLs programmatically (in linkingHelpers.ts)
 */

import {
  parseNumberParam,
  parseOptionalJsonParam,
  parseOptionalNumberParam,
  parseOptionalStringParam,
  parseStringParam,
} from './parseFunctions';

// ============================================================================
// Types
// ============================================================================

type ParseFunction = (value: string | undefined) => unknown;
type StringifyFunction = (value: any) => string;

export interface RouteConfig {
  path: string;
  parse?: Record<string, ParseFunction>;
  stringify?: Record<string, StringifyFunction>;
  screens?: Record<string, RouteConfig | string>;
}

export type RouteDefinition = string | RouteConfig;

// ============================================================================
// Shared Parse Configs
// ============================================================================

/**
 * Shared parse config for CustomerDetail route params.
 * Used by both CustomerDetailGroup and its nested CustomerDetail screen
 * to ensure deep link params are properly parsed at both levels.
 */
export const customerDetailParseConfig = {
  customerId: parseNumberParam,
  accountId: parseNumberParam,
  accountNumber: parseOptionalNumberParam,
  cpfCnpj: parseOptionalStringParam,
  cpfCnpjFormatted: parseOptionalStringParam,
  selectedTab: parseOptionalStringParam,
  movementInitialTab: parseOptionalStringParam,
  financialDetailsInitialTab: parseOptionalStringParam,
  customer: parseOptionalJsonParam,
};

// ============================================================================
// Signed-In Route Definitions
// ============================================================================

/**
 * Complete route definitions for all signed-in routes.
 * Each route can be either:
 * - A simple string path (e.g., 'cockpit')
 * - A config object with path, parse, stringify, and/or nested screens
 */
export const signedInRouteDefinitions: Record<string, RouteDefinition> = {
  // Main routes
  Home: {
    path: 'home',
    parse: {
      initialTabIndex: parseOptionalNumberParam,
    },
  },
  HomeInvestmentStrategyMobile: 'home/estrategia-investimento',
  Cockpit: 'cockpit',
  ManagementAnalysis: 'analise-gerencial',
  ManagementAnalysisDetails: {
    path: 'analise-gerencial/produtos/:produtoId',
    parse: {
      produtoId: parseOptionalStringParam,
      product: parseOptionalJsonParam,
      alertText: parseOptionalStringParam,
      distributionProductInformation: parseOptionalJsonParam,
    },
  },
  RateManagement: 'gerenciamento-taxas',
  Customers: {
    path: 'meus-clientes',
    parse: {
      search: parseOptionalStringParam,
    },
  },
  CustomersAssetsDetails: {
    path: 'meus-clientes/:customerId/:marketType/ativos',
    parse: {
      customerId: parseNumberParam,
      marketType: parseStringParam,
      accountId: parseOptionalNumberParam,
      accountNumber: parseOptionalNumberParam,
      cpfCnpj: parseOptionalStringParam,
      cpfCnpjFormatted: parseOptionalStringParam,
    },
  },
  CustomerDetailGroup: {
    path: 'meus-clientes/:customerId/:accountId/detalhe',
    parse: customerDetailParseConfig,
    screens: {
      CustomerDetail: {
        path: ':selectedTab?',
        parse: customerDetailParseConfig,
      },
      PrivatePensionApplication: {
        path: 'previdencia',
        parse: {
          accountId: parseNumberParam,
          accountNumber: parseOptionalNumberParam,
          customerId: parseNumberParam,
          cpfCnpj: parseOptionalStringParam,
          cpfCnpjFormatted: parseOptionalStringParam,
          kind: parseOptionalStringParam,
        },
      },
      SecuritiesTransferDetail: {
        path: 'transferencia-custodia/:protocolNumber',
        parse: {
          accountId: parseNumberParam,
          accountNumber: parseOptionalNumberParam,
          customerId: parseNumberParam,
          cpfCnpj: parseOptionalStringParam,
          cpfCnpjFormatted: parseOptionalStringParam,
          protocolNumber: parseStringParam,
        },
      },
    },
  },
  AdemiconConsortium: 'consorcio/ademicon',
  NewApplication: 'cockpit/boleta-facil',
  OrdersHistory: 'cockpit/minhas-ordens',
  DividendHistory: 'cockpit/proventos',
  EquityOrderEdit: 'cockpit/edicao-ordem/:orderId',
  BlockList: 'cockpit/clientes-restritos',
  Resume: 'cockpit/resumo',
  Subscription: 'cockpit/subscricoes',
  SubscriptionDetails: 'cockpit/subscricoes/:subscriptionId',
  BatchTicket: 'cockpit/boleta-lote',
  BatchTicketRequests: 'cockpit/boleta-lote/solicitacoes',
  RedeemBatchTicketDetails: 'cockpit/boleta-lote/resgates/:basketAdvisorId',
  BatchTicketResume: 'cockpit/boleta-lote/resumo',
  BatchTicketSent: 'cockpit/boleta-lote/envios/:basketAdvisorOrderId',
  FundsComparator: 'cockpit/comparador-fundos',
  AvailableProducts: 'produtos-disponiveis',
  ProductsManagement: 'controle-produtos',
  ProductsManagementDetails: {
    path: 'controle-produtos/:productId',
    parse: {
      productId: parseNumberParam,
      productFilterIdAction: parseNumberParam,
      productTypeId: parseNumberParam,
      productName: parseStringParam,
      productTypeName: parseStringParam,
      managerImage: parseOptionalStringParam,
    },
    stringify: {
      productId: (value) => value.toString(),
      productFilterIdAction: (value) => value.toString(),
      productTypeId: (value) => value.toString(),
    },
  },
  DocumentsCenter: 'central-documentos',
  Trading: 'day-trade',
  TradingDetails: {
    path: 'day-trade/conta/:accountId',
    parse: {
      accountId: parseStringParam,
      accountPNP: parseStringParam,
      sinacorAccount: parseStringParam,
      accountBP: parseStringParam,
    },
  },
  Consortium: 'consorcio',
  ConsortiumSent: 'consorcio/enviados',
  TransactionFlow: 'fluxo-movimentacoes',
  TransactionFlowDetails: {
    path: 'fluxo-movimentacoes/:product',
    parse: {
      product: parseStringParam,
    },
    stringify: {
      product: (value) => value,
    },
  },
  InviteLinkPlaceholder: 'convite',
  SignOut: 'sair',
  GenialReport: 'genial-report',
  ReportGenerator: 'gerador-de-relatorios',
  FixedFee: 'fee-fixo',

  // Angular Frame (legacy) routes
  AngularFrame_Home: 'legado/home',
  AngularFrame_Dashboard: 'legado/dashboard',
  AngularFrame_ConsolidatedPortfolio: 'legado/carteira-consolidada',
  AngularFrame_Customers: 'legado/meus-clientes',
  AngularFrame_CustomerDetail: {
    path: 'legado/meus-clientes/:DocumentoCliente/:IdConta',
    parse: {
      DocumentoCliente: parseStringParam,
      IdConta: parseNumberParam,
    },
    stringify: {
      DocumentoCliente: (value) => value,
      IdConta: (value) => value.toString(),
    },
  },
  AngularFrame_OrderGenerator: 'legado/gerador-ordens',
  AngularFrame_Commissions: 'legado/comissoes',
  AngularFrame_TransactionFlow: 'legado/fluxo-movimentacoes',
  AngularFrame_ShoppingCart: 'legado/carrinho',
  AngularFrame_AvailableProducts: 'legado/produtos-disponiveis',
  AngularFrame_ProductControl: 'legado/controle-produtos',
  AngularFrame_LifeInsurance: 'legado/seguro-vida',
  AngularFrame_AccessLevels: 'legado/niveis-acesso',
  AngularFrame_DividendHistory: 'legado/proventos',
  AngularFrame_FormsAndTerms: 'legado/fichas-termos',
  AngularFrame_VoluntaryEvents: 'legado/eventos-voluntarios',
  AngularFrame_MyProfile: 'legado/meu-perfil',
  AngularFrame_ManagementAnalysis: 'legado/analise-gerencial',
} as const;

// ============================================================================
// Signed-Out Route Definitions
// ============================================================================

export const signedOutRouteDefinitions: Record<string, RouteDefinition> = {
  SignIn: '',
  ForgotPasswordRequest: 'esqueci-minha-senha',
  ForgotPasswordCreate: 'redefinir-senha',
  UnlockConfirmation: {
    path: 'confirmacao/:confirmationToken',
    parse: {
      confirmationToken: parseStringParam,
    },
  },
  BatchTicketInvestLink: {
    path: 'token-whatsapp/:cbid',
    parse: {
      cbid: parseStringParam,
    },
  },
  FixedFeeTermAcceptanceLink: {
    path: 'fee-fixo/termo/:termId',
    parse: {
      termId: parseStringParam,
    },
  },
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extracts just the path string from a route definition.
 */
const getPathFromDefinition = (definition: RouteDefinition): string => {
  if (typeof definition === 'string') {
    return definition;
  }
  return definition.path;
};

/**
 * Generates a path-only mapping from route definitions.
 * Used by href builder functions.
 */
export const extractRoutePaths = <T extends Record<string, RouteDefinition>>(
  definitions: T
): Record<keyof T, string> => {
  const paths: Record<string, string> = {};

  for (const [routeName, definition] of Object.entries(definitions)) {
    paths[routeName] = getPathFromDefinition(definition);

    // Also extract nested screen paths (flattened)
    if (typeof definition === 'object' && definition.screens) {
      for (const [nestedName, nestedDef] of Object.entries(definition.screens)) {
        const nestedPath = getPathFromDefinition(nestedDef);
        // Construct full path for nested routes
        const fullPath = nestedPath ? `${definition.path}/${nestedPath}` : definition.path;
        paths[nestedName] = fullPath;
      }
    }
  }

  return paths as Record<keyof T, string>;
};

/**
 * Generates the linking screens config for React Navigation.
 * This is used directly in the linking configuration.
 */
export const generateLinkingScreens = (
  definitions: Record<string, RouteDefinition>
): Record<string, string | object> => {
  const screens: Record<string, string | object> = {};

  for (const [routeName, definition] of Object.entries(definitions)) {
    if (typeof definition === 'string') {
      screens[routeName] = definition;
    } else {
      const screenConfig: Record<string, unknown> = {
        path: definition.path,
      };

      if (definition.parse) {
        screenConfig.parse = definition.parse;
      }

      if (definition.stringify) {
        screenConfig.stringify = definition.stringify;
      }

      if (definition.screens) {
        screenConfig.screens = generateLinkingScreens(definition.screens);
      }

      screens[routeName] = screenConfig;
    }
  }

  return screens;
};

// ============================================================================
// Exported Path Mappings (for href builders)
// ============================================================================

/**
 * Path templates for all linkable signed-in routes.
 * Path params are denoted with `:paramName` syntax.
 */
export const signedInRoutePaths = extractRoutePaths(signedInRouteDefinitions);

/**
 * Path templates for all linkable signed-out routes.
 */
export const signedOutRoutePaths = extractRoutePaths(signedOutRouteDefinitions);

// ============================================================================
// Exported Linking Screens (for React Navigation)
// ============================================================================

/**
 * Pre-generated linking screens config for signed-in routes.
 * Used directly in SignedIn.tsx linking configuration.
 */
export const signedInLinkingScreens = generateLinkingScreens(signedInRouteDefinitions);

/**
 * Pre-generated linking screens config for signed-out routes.
 * Used directly in SignedOut.tsx linking configuration.
 */
export const signedOutLinkingScreens = generateLinkingScreens(signedOutRouteDefinitions);
